/* src/students/selectors.js */

import { createSelector } from 'reselect';
import { find, forEach, groupBy, isEmpty, map, toLower } from 'lodash';
import { loadTexts } from '../../config/texts';
import { getMoment } from '../../util/dates';
import { formatReview, reviewsData } from '../../util/misc';

export const getStudents = createSelector(
  (state) => state.students.main.data,
  (state) => state.appState.locale,
  (students, locale) =>
    map(students, (s) => {
      const texts = loadTexts('students', locale);
      const {
        _id,
        user: { first, last },
        lessons,
      } = s;
      // get
      return {
        _id,
        [texts.table.name]: `${first} ${last}`,
        [texts.table.minutes]: lessons.reduce((acc, x) => {
          if (x.status === 'COMPLETED')
            return acc + x.current.timeslots.length * 30;
          return acc;
        }, 0),
        [texts.table.completed]: lessons.reduce(
          (acc, x) => acc + (x.status === 'COMPLETED'),
          0,
        ),
      };
    }),
);

export const fetchStudentsState = createSelector(
  (state) => state.students.main.loading,
  (state) => state.students.main.error,
  (loading, error) => ({ loading, error }),
);

export const getStudentsListRange = createSelector(
  (state) => state.students.browse,
  ({ start, end, count }) => ({ start, end, count }),
);

export const getSelected = createSelector(
  (state) => state.students.single.data,
  (state) => state.appState.locale,
  (student, locale) => {
    const texts = loadTexts('students', locale);
    if (isEmpty(student)) {
      return {
        _id: '',
        name: texts.profile.none.no_name,
        email: texts.profile.none.no_email,
        phone: texts.profile.none.no_phone,
        bio: texts.profile.none.no_bio,
        avatar: '',
        active: false,
        empty: true,
      };
    }
    const {
      _id,
      bio,
      user: { first, last, email, phone, active, avatar, howDidYouHear },
    } = student;
    return {
      _id,
      name: `${first} ${last}`,
      email,
      phone,
      avatar,
      bio,
      active,
      hearAboutUs: howDidYouHear,
    };
  },
);

export const getNotesList = createSelector(
  (state) => state.students.single.data,
  (student) => {
    if (isEmpty(student)) return [];
    const notes = map(student.user.notes, (n) => {
      const {
        _id,
        admin: { user },
        body,
        createdAt,
      } = n;
      const date = getMoment(createdAt.timestamp);
      return {
        _id,
        author: `${user.first} ${user.last}`,
        body,
        date,
      };
    });
    notes.sort((a, b) => a.date.valueOf() - b.date.valueOf());
    return map(notes, ({ date, ...rest }) => ({
      ...rest,
      date: date.format('MMM D, Y'),
    }));
  },
);

export const fetchStudentState = createSelector(
  (state) => state.students.single,
  (students) => ({ loading: false, error: '' }),
);

export const getLessons = createSelector(
  (state) => state.students.single.data,
  (state) => state.appState.locale,
  (data, locale) => {
    const texts = loadTexts('students', locale);
    const {
      profile: { lessons },
    } = texts;
    if (isEmpty(data)) return [];
    return map(data.lessons, ({ _id, course, group: { tutor }, current }) => {
      const {
        user: { first, last },
      } = tutor;
      const d = {
        _id,
        [lessons.tutor]: `${first} ${last}`,
        [lessons.date]: getMoment(current.startTime.timestamp).format(
          'MMM D, Y',
        ),
        [lessons.course]: course
          ? `${course.subject} ${course.number}`
          : texts.profile.lessons.no_course,
      };

      return d;
    });
  },
);

export const getReviewsList = createSelector(
  (state) => state.students.single.data,
  (state) => state.appState.locale,
  (student, locale) => {
    if (isEmpty(student)) return [];

    const reviews = student.reviewsGiven.map((r) => formatReview(r, 'to'));
    reviews.sort((a, b) => a.date.valueOf() - b.date.valueOf());

    return reviews;
  },
);

export const getFeedbackList = createSelector(
  (state) => state.students.single.data,
  (state) => state.appState.locale,
  (student, locale) => {
    if (isEmpty(student)) return [];

    const reviews = student.reviews.map((r) => formatReview(r));
    reviews.sort((a, b) => a.date.valueOf() - b.date.valueOf());

    return reviews;
  },
);

export const getReviewsData = createSelector(
  (state) => state.students.single.data,
  (state) => state.appState.locale,
  (student, locale) => {
    const texts = loadTexts('students', locale);
    const def = [
      { title: texts.profile.reviews.reviews_given, value: '0' },
      { title: texts.profile.reviews.knowledge_given, value: '0' },
      { title: texts.profile.reviews.helpfulness_given, value: '0' },
      { title: texts.profile.reviews.organization_given, value: '0' },
      { title: texts.profile.reviews.rating_given, value: '0' },
    ];
    if (isEmpty(student)) return def;

    // to/from doesn't matter here
    const reviews = student.reviewsGiven.map((r) => formatReview(r));

    const data = reviewsData(reviews);

    const out = [
      { title: texts.profile.reviews.reviews_given, value: `${data.count}` },
      ...Object.keys(data.averages).map((key) => ({
        title: `${texts.profile.reviews.average_category}: ${key}`,
        value: data.averages[key].toFixed(2),
      })),
    ];

    if (data.overall !== -1) {
      out.push({
        title: texts.profile.reviews.rating_given,
        value: `${data.overall.toFixed(2)}`,
      });
    }

    return out;
  },
);

export const getAnalyticsData = createSelector(
  (state) => state.students.single.data,
  (state) => state.appState.locale,
  (student, locale) => {
    const texts = loadTexts('students', locale);
    const {
      profile: { analytics },
    } = texts;
    if (isEmpty(student)) return [];
    const { lessons } = student;
    const completed = lessons.filter((l) => l.status === 'COMPLETED');
    const group = groupBy(completed, (l) => l.group.tutor._id);

    const totalDuration = completed.reduce(
      (acc, x) => acc + x.current.timeslots.length * 30,
      0,
    );
    const aveDuration =
      completed.length === 0 ? 0 : totalDuration / completed.length;

    const totalPrice = completed.reduce(
      (acc, x) => acc + x.price + x.studentFee,
      0,
    );
    const avePrice = completed.length === 0 ? 0 : totalPrice / completed.length;

    const aveHrSpent =
      totalDuration === 0 ? 0 : (totalPrice / totalDuration) * 60;

    const referred = student.referred ? student.referred.length : 0;

    return [
      {
        title: analytics.lesson_duration,
        value: `${aveDuration} ${analytics.minutes}`,
      },
      {
        title: analytics.lesson_price,
        value: `${(avePrice / 100).toFixed(2)}`,
      },
      {
        title: analytics.hourly_spending,
        value: `${(aveHrSpent / 100).toFixed(2)}`,
      },
      {
        title: analytics.distinct_tutors,
        value: `${Object.keys(group).length}`,
      },
      { title: analytics.referred_students, value: `${referred}` },
    ];
  },
);

export const getPaymentInfo = createSelector(
  (state) => state.students.single.data,
  (state) => state.appState.locale,
  (student, locale) => {
    const texts = loadTexts('students', locale);
    const {
      profile: { payment },
    } = texts;
    if (isEmpty(student)) return [];
    const { cards, credits } = student;
    // finde default card
    const def = find(cards, (c) => c.default);
    const last4 = def ? `xxxxx${def.last4}` : payment.no_card;

    return [
      { title: payment.default, value: last4 },
      { title: payment.number, value: `${cards.length}` },
      { title: payment.credits, value: `${(credits / 100).toFixed(2)}` },
    ];
  },
);

export const getPaymentErrors = createSelector(
  (state) => state.students.single.data,
  (student) => {
    if (isEmpty(student)) return [];
    const errors = map(student.user.paymentErrors, (p) => ({
      title: getMoment(p.createdAt.timestamp),
      value: p.code,
    }));
    errors.sort((a, b) => a.title.valueOf() - b.title.valueOf());
    return map(errors, ({ title, ...rest }) => ({
      ...rest,
      title: title.format('MMM D, YYYY'),
    }));
  },
);



// WEBPACK FOOTER //
// ./src/modules/students/selectors.js